import { Feature } from "ol";
import { FeatureType, GamePointStatus, SelectedStatus } from "./enums";
import { forEachFeatureOfLayer, getLayerById, zoomToExtent, zoomToFeature } from "./mapFunctions";
import { extend, createEmpty, isEmpty } from "ol/extent";

export const selectGame = (feature: Feature, callback): void => {
    unselectGameMainPoints();
    unselectGameCheckpoints();
    unselectGameTracks();
    callback?.(feature);
    if (!feature) {
        return;
    }

    zoomToGame(feature, () => {
        selectGameMainPoint(feature);
        selectGameTrack(feature);
        showGamePoints(feature);
    });
};

export const selectGameUsingId = (selectGameById, callback): void => {
    let gameFeature = undefined;
    if (selectGameById && selectGameById.gameId !== undefined) {
        const gameId = selectGameById.gameId;
        getLayerById(FeatureType.GameMainPoint)
            .getSource()
            .forEachFeature((f: Feature) => f.get("GAMEID") === Number(gameId) && (gameFeature = f));
    }
    selectGame(gameFeature, callback);
};

export const startGame = (feature: Feature): void => {
    if (feature) {
        showHideGameMainPoints(false);
        feature.setProperties({
            GAMEPOINTSTATUS: GamePointStatus.Started,
            SELECTED: SelectedStatus.Selected,
            HIDDEN: true
        });
    } else if (feature === null) {
        unselectGameMainPoints();
        unselectGameCheckpoints();
        unselectGameTracks();
        showHideGameMainPoints(true);
    }
};

export const finishGame = (feature: Feature): void => {
    console.log("FINISH");
    feature.setProperties({
        GAMEPOINTSTATUS: GamePointStatus.Completed,
        SELECTED: SelectedStatus.Default,
        HIDDEN: false
    });
    unselectGameMainPoints();
    unselectGameCheckpoints();
    unselectGameTracks();
    showHideGameMainPoints(true);
};

export const selectGameMainPoint = (feature: Feature): void => feature.set("SELECTED", SelectedStatus.Selected);

export const selectGameCheckPoint = (feature: Feature, callback): void => {
    const oid = feature.get("OBJECTID");
    forEachFeatureOfLayer(FeatureType.GameCheckPoint, (f: Feature) => {
        const isSelected = f.get("OBJECTID") === oid;
        f.set("SELECTED", isSelected ? SelectedStatus.Selected : SelectedStatus.Default);
        isSelected && callback?.(f);
    });
};

export const selectGameCheckPointUsingId = (selectGameCheckPointById, callback): void => {
    if (!selectGameCheckPointById.checkPointId) {
        forEachFeatureOfLayer(FeatureType.GameCheckPoint, (f: Feature) =>
            f.setProperties({ SELECTED: SelectedStatus.Default })
        );
        return;
    }

    getLayerById(FeatureType.GameCheckPoint)
        .getSource()
        .forEachFeature(
            (f: Feature) =>
                f.get("POINTID") === Number(selectGameCheckPointById.checkPointId) && selectGameCheckPoint(f, callback)
        );
};

export const unselectGameCheckpoints = (): void =>
    forEachFeatureOfLayer(FeatureType.GameCheckPoint, (f: Feature) =>
        f.setProperties({ SELECTED: SelectedStatus.Default, GAMESELECTED: 0, APPROACHED: false })
    );

export const unselectGameMainPoints = (): void =>
    forEachFeatureOfLayer(FeatureType.GameMainPoint, (f: Feature) =>
        f.setProperties({ SELECTED: SelectedStatus.Default, GAMESELECTED: 0 })
    );

export const showHideGameMainPoints = (isVisible: boolean): void =>
    forEachFeatureOfLayer(FeatureType.GameMainPoint, (f: Feature) => f.set("HIDDEN", !isVisible));

export const unselectGameTracks = (): void =>
    forEachFeatureOfLayer(FeatureType.GameTrack, (f: Feature) => f.set("SELECTED", SelectedStatus.Default));

export const showGamePoints = (feature: Feature): void => {
    const gameId = feature.get("GAMEID");
    forEachFeatureOfLayer(FeatureType.GameCheckPoint, (f: Feature) => {
        const isGameFeature = f.get("GAMEID") === gameId;
        f.setProperties({
            SELECTED: SelectedStatus.Default,
            GAMESELECTED: isGameFeature ? 1 : 0
        });
    });
};

export const setCheckPointFound = (feature: Feature): void => {
    feature.setProperties({ GAMEPOINTSTATUS: GamePointStatus.Completed, APPROACHED: false });
};

export const selectGameTrack = (feature: Feature): void => {
    const gameId = feature.get("GAMEID");
    forEachFeatureOfLayer(FeatureType.GameTrack, (f: Feature) => {
        if (f.get("GAMEID") === gameId) {
            f.set("SELECTED", SelectedStatus.Selected);
        }
    });
};

export const zoomToGame = (feature: Feature, callback: CallableFunction): void => {
    const gameId = feature.get("GAMEID");
    const gameExtent = createEmpty();
    forEachFeatureOfLayer(
        FeatureType.GameTrack,
        (f: Feature) => f.get("GAMEID") === gameId && extend(gameExtent, f.getGeometry().getExtent())
    );
    isEmpty(gameExtent) ? zoomToFeature(feature, callback) : zoomToExtent(gameExtent, callback);
};
